import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithGoogle, observeAuthState } from '../services/authService';
import Navbar from '../components/Navbar';

const AdminLoginPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Set up auth state observer
    const unsubscribe = observeAuthState((user, isAdmin) => {
      if (user && isAdmin) {
        navigate('/admin/dashboard');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await signInWithGoogle();
      
      if (result.error) {
        setError(result.error);
      } else if (!result.isAdmin) {
        setError("You don't have permission to access the admin panel.");
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="container-custom mx-auto py-16">
        <div className="max-w-md mx-auto bg-white shadow-lg rounded-lg p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-800 mb-2">Admin Login</h1>
            <p className="text-gray-600">Sign in to access the property management dashboard</p>
          </div>
          
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          )}
          
          <div className="flex flex-col space-y-4">
            <button 
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="flex items-center justify-center w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              {loading ? (
                <span className="inline-block h-5 w-5 border-2 border-gray-400 border-t-primary rounded-full animate-spin mr-2"></span>
              ) : (
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M21.35,11.1H12v3.8h5.3c-0.35,2-1.65,3.6-3.55,4.3v2.9h4.8C21.2,18.6,22,16,22,13.2C22,11.9,21.75,11.4,21.35,11.1z"
                  />
                  <path
                    fill="#34A853"
                    d="M12,21.35c-3.8,0-6.9-1.35-9-3.25v-2.9c2.8,1.8,5.5,2.3,8.8,0.7C13.1,14.8,13.9,14.5,14.7,14.2L12,21.35z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M3,13.55c0-1.15,0.25-2.2,0.65-3.05V7.5C1.8,9,1,10.75,1,13.25c0,2.2,0.6,4.05,1.65,5.5L3,13.55z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12,5.75c2.2,0,4.25,0.8,5.75,2.05l2.35-2.35C18.15,3.6,15.25,2,12,2C7.8,2,4.1,4.2,2.15,7.5l2.85,2.1C5.8,7.5,8.7,5.75,12,5.75z"
                  />
                </svg>
              )}
              {loading ? "Signing in..." : "Sign in with Google"}
            </button>
            
            <p className="text-sm text-gray-500 text-center mt-4">
              Note: Only authorized administrators can access this panel.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;